import React, { useMemo, useState, useEffect } from "react";
import { toTitleCaseArea } from "../../../../constants";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";

const normalizeAreaName = (value) => {
  try {
    return String(value || "")
      .trim()
      .replace(/\s+/g, " ");
  } catch {
    return "";
  }
};

const reservedAreaNames = new Set(["all", "nearby"]);

const isReservedArea = (name) => {
  const normalized = normalizeAreaName(name);
  if (!normalized) return false;
  return reservedAreaNames.has(normalized.toLowerCase());
};

/**
 * BulkAreaModal
 * - freeSolo Area select/type
 * - Update disabled until non-empty and not reserved
 */
const BulkAreaModal = ({ open, onClose, onUpdate, areas = [], loading }) => {
  const selectableAreas = useMemo(() => {
    return (areas || []).filter((a) => !isReservedArea(a?.name));
  }, [areas]);

  // value can be string (typed) or an Area object
  const [areaValue, setAreaValue] = useState("");

  useEffect(() => {
    if (!open) return;
    // reset whenever opened
    setAreaValue("");
  }, [open]);

  const areaNameNormalized = useMemo(() => {
    if (!areaValue) return "";
    if (typeof areaValue === "string") return normalizeAreaName(areaValue);
    return normalizeAreaName(areaValue?.name);
  }, [areaValue]);

  const reservedError = !!areaNameNormalized && isReservedArea(areaNameNormalized);

  const updateEnabled = !!areaNameNormalized && !reservedError && !loading;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Update Area</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Select an Area or type a new one. This will overwrite the Area for all
            selected QuickTales.
          </Typography>

          <Autocomplete
            value={typeof areaValue === "string" ? null : areaValue}
            inputValue={
              typeof areaValue === "string" ? areaValue : areaValue?.name || ""
            }
            freeSolo
            options={selectableAreas}
            getOptionLabel={(option) => option?.name || ""}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            onChange={(event, newValue) => setAreaValue(newValue)}
            onInputChange={(event, newInputValue, reason) => {
              if (reason === "input" && event?.target) {
                const { selectionStart, value } = event.target;
                // Only apply title case when typing forward (cursor at end)
                if (selectionStart === value.length) {
                  setAreaValue(toTitleCaseArea(newInputValue));
                  return;
                }
              }
              setAreaValue(newInputValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select or Type Area"
                error={reservedError}
                helperText={
                  reservedError
                    ? '"All" and "Nearby" are reserved Area names.'
                    : ""
                }
              />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => onUpdate(areaNameNormalized)}
          disabled={!updateEnabled}
        >
          {loading ? "Updating..." : "Update"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkAreaModal;
